import React, { useState, useEffect, useCallback } from 'react';
import OverlayLayer from './OverlayLayer.jsx';

// Annotations: arrow, text, rect, ellipse, freehand, pin. Click-to-create.
// activeKind drives creation; null = view only.
export default function AnnotationLayer({
  annotations = [],
  activeKind = null,
  imageWidth, imageHeight,
  canvasWidth, canvasHeight,
  panX, panY, zoom, rotation,
  onCreate,
  onTextInput, // (payload) => opens text prompt, then calls onCreate with filled text
}) {
  const [partial, setPartial] = useState(null);

  useEffect(() => { setPartial(null); }, [activeKind]);

  const onPointerDown = useCallback((pt) => {
    if (!activeKind) return;
    if (activeKind === 'pin' || activeKind === 'text') {
      const payload = { kind: activeKind, geometry: { points: [pt] }, color: '#2dd4a0' };
      if (activeKind === 'text') { onTextInput?.(payload); return; }
      onCreate?.(payload);
      return;
    }
    // 2-point shapes: arrow, rect, ellipse
    if (['arrow', 'rect', 'ellipse'].includes(activeKind)) {
      if (!partial) { setPartial({ kind: activeKind, points: [pt] }); return; }
      onCreate?.({ kind: activeKind, geometry: { points: [partial.points[0], pt] }, color: '#2dd4a0' });
      setPartial(null);
      return;
    }
  }, [activeKind, partial, onCreate, onTextInput]);

  const overlayProps = { imageWidth, imageHeight, canvasWidth, canvasHeight, panX, panY, zoom, rotation };
  const sw = Math.max(0.5, 1.5 / Math.max(0.1, (Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight) * zoom)));
  const textSize = Math.max(8, 14 / Math.max(0.1, (Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight) * zoom)));

  return (
    <OverlayLayer {...overlayProps} interactive={!!activeKind} onPointerDown={onPointerDown}>
      {annotations.map(a => <Annotation key={a.id} a={a} sw={sw} textSize={textSize} />)}
      {partial && (
        <circle cx={partial.points[0][0]} cy={partial.points[0][1]} r={sw * 2} fill="#2dd4a0" />
      )}
    </OverlayLayer>
  );
}

function Annotation({ a, sw, textSize }) {
  const color = a.color || '#2dd4a0';
  const g = a.geometry || {};
  const pts = g.points || [];
  switch (a.kind) {
    case 'pin':
      if (!pts[0]) return null;
      return (
        <g>
          <circle cx={pts[0][0]} cy={pts[0][1] - sw * 10} r={sw * 3} fill={color} />
          <line x1={pts[0][0]} y1={pts[0][1] - sw * 7} x2={pts[0][0]} y2={pts[0][1]} stroke={color} strokeWidth={sw} />
        </g>
      );
    case 'text':
      if (!pts[0]) return null;
      return (
        <text x={pts[0][0]} y={pts[0][1]} fill={color} fontSize={textSize} fontFamily="'Instrument Sans', sans-serif"
              paintOrder="stroke" stroke="rgba(0,0,0,0.6)" strokeWidth={sw * 0.5}>
          {a.text || '…'}
        </text>
      );
    case 'arrow':
      if (pts.length < 2) return null;
      return <Arrow from={pts[0]} to={pts[1]} color={color} sw={sw} />;
    case 'rect':
      if (pts.length < 2) return null;
      return <rect x={Math.min(pts[0][0], pts[1][0])} y={Math.min(pts[0][1], pts[1][1])}
                   width={Math.abs(pts[1][0] - pts[0][0])} height={Math.abs(pts[1][1] - pts[0][1])}
                   stroke={color} strokeWidth={sw} fill="none" />;
    case 'ellipse':
      if (pts.length < 2) return null;
      return (
        <ellipse cx={(pts[0][0] + pts[1][0]) / 2} cy={(pts[0][1] + pts[1][1]) / 2}
                 rx={Math.abs(pts[1][0] - pts[0][0]) / 2} ry={Math.abs(pts[1][1] - pts[0][1]) / 2}
                 stroke={color} strokeWidth={sw} fill="none" />
      );
    case 'freehand':
      if (pts.length < 2) return null;
      return <polyline points={pts.map(p => p.join(',')).join(' ')} stroke={color} strokeWidth={sw} fill="none" />;
    default:
      return null;
  }
}

function Arrow({ from, to, color, sw }) {
  const ang = Math.atan2(to[1] - from[1], to[0] - from[0]);
  const head = sw * 10;
  const ax = to[0] - head * Math.cos(ang - Math.PI / 6);
  const ay = to[1] - head * Math.sin(ang - Math.PI / 6);
  const bx = to[0] - head * Math.cos(ang + Math.PI / 6);
  const by = to[1] - head * Math.sin(ang + Math.PI / 6);
  return (
    <g>
      <line x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]} stroke={color} strokeWidth={sw} />
      <polygon points={`${to[0]},${to[1]} ${ax},${ay} ${bx},${by}`} fill={color} />
    </g>
  );
}
